import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import Box from '@material-ui/core/Box';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import update from '@scoir/immutability';
import isUndefined from 'lodash/isUndefined';
import Paper from '@material-ui/core/Paper';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import CheckMarkSecondary from '@scoir/brand-icons/dist/CheckMarkSecondary';
import CancelSecondary from '@scoir/brand-icons/dist/CancelSecondary';
import { jsx, jsxs } from 'react/jsx-runtime';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import size from 'lodash/size';
import isEmpty from 'lodash/isEmpty';
import reject from 'lodash/reject';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import TableContainer from '@material-ui/core/TableContainer';
import get from 'lodash/get';
import has from 'lodash/has';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';

const iconStyle = theme => ({
  borderRadius: '50%',
  fontSize: theme.typography.pxToRem(16),
  padding: 3,
  color: '#fff'
});
const useStyles$7 = makeStyles(theme => {
  return {
    container: {
      display: 'inline-block',
      verticalAlign: 'baseline'
    },
    error: {
      color: theme.palette.error.main,
      fontSize: theme.typography.pxToRem(24)
    },
    matchedField: {
      backgroundColor: theme.palette.success.main,
      ...iconStyle(theme)
    },
    warningField: {
      backgroundColor: theme.palette.error.main,
      ...iconStyle(theme)
    }
  };
});
const RowIndicator = _ref => {
  let {
    isError,
    isDuplicate,
    isMatched,
    className
  } = _ref;
  const classes = useStyles$7();
  return /*#__PURE__*/jsx("div", {
    className: cn(classes.container, className),
    children: isError ? /*#__PURE__*/jsx("div", {
      className: classes.error,
      "aria-label": "error",
      children: "*"
    }) : !isDuplicate && isMatched ? /*#__PURE__*/jsx(CheckMarkSecondary, {
      className: classes.matchedField,
      "aria-label": "matched"
    }) : /*#__PURE__*/jsx(CancelSecondary, {
      className: classes.warningField,
      "aria-label": "unmatched"
    })
  });
};
RowIndicator.propTypes = {
  isError: PropTypes.bool,
  isDuplicate: PropTypes.bool,
  isMatched: PropTypes.bool,
  className: PropTypes.string
};

const useStyles$6 = makeStyles(theme => ({
  wrapper: {
    display: 'inline-block'
  },
  icon: {
    paddingRight: theme.spacing(1)
  },
  notRequired: {
    minWidth: 220,
    paddingLeft: theme.spacing(2)
  }
}));
const FieldCount = _ref => {
  let {
    isError,
    isMatched,
    isRequired,
    isDuplicate,
    count,
    label
  } = _ref;
  const classes = useStyles$6();
  const isPlural = count !== 1;
  const pluralizedLabel = label.replace('(s)', isPlural ? 's' : '');
  return /*#__PURE__*/jsxs("span", {
    className: cn(classes.wrapper, {
      [classes.notRequired]: !isRequired
    }),
    children: [(!isUndefined(isError) || !isUndefined(isDuplicate) || !isUndefined(isMatched)) && /*#__PURE__*/jsx(RowIndicator, {
      className: classes.icon,
      isError: isError,
      isDuplicate: isDuplicate,
      isMatched: isMatched
    }), /*#__PURE__*/jsx("strong", {
      children: count
    }), " ", pluralizedLabel]
  });
};
FieldCount.propTypes = {
  isError: PropTypes.bool,
  isMatched: PropTypes.bool,
  isRequired: PropTypes.bool,
  isDuplicate: PropTypes.bool,
  count: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired
};

const StyledTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#fff',
    color: '#555',
    width: 250,
    boxShadow: '0 2px 5px 0 #555',
    fontSize: theme.typography.pxToRem(16)
  }
}))(Tooltip);

const useStyles$5 = makeStyles(theme => ({
  row: {
    margin: theme.spacing(1)
  },
  rowIndicator: {
    marginRight: theme.spacing(1)
  }
}));
const RequiredFields = _ref => {
  let {
    requirements,
    children
  } = _ref;
  const classes = useStyles$5();
  const [open, setOpen] = useState(false);
  return /*#__PURE__*/jsx(ClickAwayListener, {
    onClickAway: () => setOpen(false),
    children: /*#__PURE__*/jsx(StyledTooltip, {
      id: "required-fields-tooltip",
      placement: "top",
      onClose: () => setOpen(false),
      open: open,
      disableHoverListener: true,
      disableTouchListener: true,
      title: /*#__PURE__*/jsx(Box, {
        variant: "body1",
        "aria-live": open ? 'polite' : 'off',
        children: Object.values(requirements).map((requirement, key) => /*#__PURE__*/jsxs("div", {
          className: classes.row,
          children: [/*#__PURE__*/jsx(RowIndicator, {
            className: classes.rowIndicator,
            isDuplicate: requirement.isDuplicate,
            isMatched: requirement.isMatched
          }), /*#__PURE__*/jsx("strong", {
            children: requirement.label
          }), /*#__PURE__*/jsx(Box, {
            component: "span",
            ml: 1,
            children: requirement.isDuplicate && /*#__PURE__*/jsxs("span", {
              children: [/*#__PURE__*/jsx("span", {
                "aria-hidden": "true",
                children: " *"
              }), " duplicates"]
            })
          })]
        }, key))
      }),
      children: /*#__PURE__*/jsx(Button, {
        variant: "outlined",
        onClick: () => setOpen(!open),
        children: /*#__PURE__*/jsx(Typography, {
          variant: "button",
          children: children
        })
      })
    })
  });
};
RequiredFields.propTypes = {
  requirements: PropTypes.object.isRequired,
  children: PropTypes.object.isRequired
};

const useStyles$4 = makeStyles(theme => ({
  container: {
    textAlign: 'left'
  },
  paper: {
    padding: theme.spacing(2)
  }
}));
const Legend = _ref => {
  let {
    matchedCount,
    duplicateTotal,
    unmatchedCount,
    missingRequiredCount,
    requirements,
    importFieldsWithDuplicates
  } = _ref;
  const classes = useStyles$4();
  return /*#__PURE__*/jsx(Paper, {
    className: classes.paper,
    children: /*#__PURE__*/jsxs("div", {
      className: classes.container,
      children: [!isUndefined(requirements) && /*#__PURE__*/jsx(RequiredFields, {
        requirements: requirements,
        children: /*#__PURE__*/jsx(FieldCount, {
          isRequired: true,
          count: missingRequiredCount,
          label: "Required Field(s) Missing"
        })
      }), /*#__PURE__*/jsx(FieldCount, {
        isDuplicate: false,
        isMatched: true,
        count: isUndefined(importFieldsWithDuplicates) ? matchedCount : matchedCount - importFieldsWithDuplicates,
        label: "Field(s) Matched"
      }), /*#__PURE__*/jsx(FieldCount, {
        isDuplicate: false,
        isMatched: false,
        count: unmatchedCount,
        label: "Field(s) Unmatched"
      }), !isUndefined(duplicateTotal) && /*#__PURE__*/jsx(FieldCount, {
        isError: true,
        count: duplicateTotal,
        label: "Duplicate(s) Found"
      })]
    })
  });
};
Legend.propTypes = {
  matchedCount: PropTypes.number.isRequired,
  duplicateTotal: PropTypes.number,
  unmatchedCount: PropTypes.number.isRequired,
  missingRequiredCount: PropTypes.number,
  importFieldsWithDuplicates: PropTypes.number,
  requirements: PropTypes.object
};

const useStyles$3 = makeStyles(theme => ({
  container: {
    fontStyle: 'italic',
    display: 'flex',
    height: 36,
    alignItems: 'center'
  },
  error: {
    color: theme.palette.error.main
  }
}));
const ImportStatus = _ref => {
  let {
    isDuplicate,
    isMatched,
    canIgnore
  } = _ref;
  const classes = useStyles$3();
  const notMatchedText = canIgnore ? 'Will Be Ignored' : 'Must Select Value';
  return /*#__PURE__*/jsx("div", {
    className: classes.container,
    children: isDuplicate ? /*#__PURE__*/jsx("div", {
      className: classes.error,
      children: /*#__PURE__*/jsxs("span", {
        children: [/*#__PURE__*/jsx("span", {
          "aria-hidden": "true",
          children: "*"
        }), " Duplicate Found"]
      })
    }) : !isMatched && notMatchedText
  });
};
ImportStatus.propTypes = {
  isMatched: PropTypes.bool,
  isDuplicate: PropTypes.bool,
  canIgnore: PropTypes.bool.isRequired
};
ImportStatus.defaultProps = {
  isMatched: false,
  isDuplicate: false,
  canIgnore: true
};

const useStyles$2 = makeStyles(theme => ({
  formControl: {
    width: 200
  },
  required: {
    display: 'inline-block',
    marginLeft: theme.spacing(1)
  }
}));
const MappableSelector = _ref => {
  let {
    onChange,
    value,
    mappableItems,
    defaultValue,
    error
  } = _ref;
  const classes = useStyles$2();
  return /*#__PURE__*/jsxs(Box, {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    children: [/*#__PURE__*/jsx(FormControl, {
      className: classes.formControl,
      error: error,
      children: /*#__PURE__*/jsxs(Select, {
        variant: "outlined",
        value: value,
        "aria-invalid": "error",
        onChange: e => onChange(parseInt(e.target.value)),
        children: [/*#__PURE__*/jsx(MenuItem, {
          value: defaultValue,
          children: "\u2014"
        }), mappableItems.map((item, idx) => /*#__PURE__*/jsxs(MenuItem, {
          value: idx,
          children: [item.Label, item.Required && /*#__PURE__*/jsxs("span", {
            className: classes.required,
            children: [/*#__PURE__*/jsx("span", {
              "aria-hidden": "true",
              children: " * "
            }), /*#__PURE__*/jsx("span", {
              children: "(Required)"
            })]
          })]
        }, idx))]
      })
    }), /*#__PURE__*/jsx(IconButton, {
      "aria-label": "clear selection",
      disabled: value === defaultValue,
      onClick: () => onChange(defaultValue),
      children: /*#__PURE__*/jsx(CancelSecondary, {})
    })]
  });
};
MappableSelector.propTypes = {
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  mappableItems: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool.isRequired
};
MappableSelector.defaultProps = {
  defaultValue: -1
};

const useStyles$1 = makeStyles({
  exampleValues: {
    listStyle: 'none'
  },
  listWrapper: {
    padding: 0
  }
});
const ImportField = _ref => {
  let {
    field,
    isMatched,
    isDuplicate
  } = _ref;
  const classes = useStyles$1();
  const values = reject(field.Values, isEmpty);
  return /*#__PURE__*/jsx(List, {
    children: /*#__PURE__*/jsxs(ListItem, {
      alignItems: "flex-start",
      children: [/*#__PURE__*/jsx(ListItemIcon, {
        children: /*#__PURE__*/jsx(RowIndicator, {
          isMatched: isMatched,
          isDuplicate: isDuplicate
        })
      }), /*#__PURE__*/jsx(ListItemText, {
        primary: /*#__PURE__*/jsx(Typography, {
          variant: "body1",
          children: !isEmpty(field.Header) ? field.Header : 'Empty Header'
        }),
        secondary: /*#__PURE__*/jsx("ul", {
          className: classes.listWrapper,
          children: size(values) > 0 && values.map((value, idx) => /*#__PURE__*/jsx(Typography, {
            className: classes.exampleValues,
            component: "li",
            children: value
          }, idx))
        })
      })]
    })
  });
};
ImportField.propTypes = {
  field: PropTypes.object.isRequired,
  isMatched: PropTypes.bool.isRequired,
  isDuplicate: PropTypes.bool.isRequired
};

const ColumnMapperRow = _ref => {
  let {
    duplicateCount,
    field,
    mappableItems,
    onSelectMappedIndex
  } = _ref;
  const isDuplicate = duplicateCount > 0;
  const isMatched = field.MappedIndex !== -1;
  return /*#__PURE__*/jsxs(TableRow, {
    children: [/*#__PURE__*/jsx(TableCell, {
      children: /*#__PURE__*/jsx(ImportField, {
        isDuplicate: isDuplicate,
        field: field,
        isMatched: isMatched
      })
    }), /*#__PURE__*/jsx(TableCell, {
      children: /*#__PURE__*/jsxs(Grid, {
        container: true,
        direction: "row",
        children: [/*#__PURE__*/jsx(Grid, {
          item: true,
          xs: 6,
          children: /*#__PURE__*/jsx(MappableSelector, {
            error: isDuplicate,
            value: field.MappedIndex,
            mappableItems: mappableItems,
            onChange: mappedIndex => onSelectMappedIndex(mappedIndex)
          })
        }), /*#__PURE__*/jsx(Grid, {
          item: true,
          xs: 2,
          children: /*#__PURE__*/jsx(Box, {
            mt: 1,
            ml: 1,
            children: /*#__PURE__*/jsx(ImportStatus, {
              isDuplicate: isDuplicate,
              isMatched: isMatched
            })
          })
        })]
      })
    })]
  });
};
ColumnMapperRow.propTypes = {
  duplicateCount: PropTypes.number,
  field: PropTypes.object.isRequired,
  mappableItems: PropTypes.array.isRequired,
  onSelectMappedIndex: PropTypes.func.isRequired
};

const useStyles = makeStyles({
  container: {
    maxHeight: '65vh'
  }
});
const Container = _ref => {
  let {
    children
  } = _ref;
  const classes = useStyles();
  return /*#__PURE__*/jsx(TableContainer, {
    className: classes.container,
    component: Paper,
    children: children
  });
};
Container.propTypes = {
  children: PropTypes.any.isRequired
};

const getAllowedDuplicates = mappableItems => {
  const allowedDupes = [];
  forEach(mappableItems, (field, idx) => {
    if (get(field, 'AllowMultiple', false)) {
      allowedDupes.push(idx);
    }
  });
  return allowedDupes;
};
const getRequirements = mappableItems => {
  const requirements = {};
  forEach(mappableItems, (field, idx) => {
    if (get(field, 'Required', false)) {
      requirements[idx] = {
        label: field.Label,
        isMatched: false,
        isDuplicate: false
      };
    }
  });
  return requirements;
};
const getCleanRequirements = oldRequirements => {
  const requirements = {};
  forEach(Object.keys(oldRequirements), key => {
    requirements[key] = {
      ...oldRequirements[key],
      isMatched: false,
      isDuplicate: false
    };
  });
  return requirements;
};
const getFieldCounts = function (fields, oldRequirements) {
  let allowedDuplicates = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  const requirements = getCleanRequirements(oldRequirements);
  const duplicateCounts = {};
  let duplicateTotal = 0;
  let unmatchedCount = 0;
  let matchedCount = 0;
  let importFieldsWithDuplicates = 0;
  let missingRequiredCount = Object.keys(requirements).length;
  forEach(fields, field => {
    const mappedIdx = field.MappedIndex;
    const isRequired = has(requirements, mappedIdx);
    const isMatched = mappedIdx !== -1;
    const isDuplicate = isMatched && has(duplicateCounts, mappedIdx) && !includes(allowedDuplicates, mappedIdx);
    if (isDuplicate) {
      duplicateTotal++;
      duplicateCounts[mappedIdx]++;
    } else {
      if (isMatched) {
        duplicateCounts[mappedIdx] = 0;
        matchedCount++;
      } else {
        unmatchedCount++;
      }
    }
    if (isRequired) {
      const req = requirements[mappedIdx];
      req.isMatched = isMatched;
      req.isDuplicate = isDuplicate;
    }
  });
  forEach(duplicateCounts, value => {
    if (value > 0) {
      importFieldsWithDuplicates++;
    }
  });
  forEach(requirements, val => {
    if (val.isMatched && !val.isDuplicate) {
      missingRequiredCount--;
    }
  });
  const isValid = duplicateTotal === 0 && missingRequiredCount === 0;
  return {
    duplicateCounts,
    duplicateTotal,
    matchedCount,
    missingRequiredCount,
    unmatchedCount,
    requirements,
    isValid,
    importFieldsWithDuplicates
  };
};

class ColumnMapper extends React.Component {
  constructor(props) {
    super(props);
    const requirements = getRequirements(props.mappableItems);
    const allowedDuplicates = getAllowedDuplicates(props.mappableItems);
    this.state = {
      duplicateCounts: {},
      duplicateTotal: 0,
      isValid: false,
      matchedCount: 0,
      missingRequiredCount: 0,
      requirements,
      unmatchedCount: 0,
      allowedDuplicates,
      importFieldsWithDuplicates: 0
    };
  }
  static propTypes = {
    importTypeLabel: PropTypes.string.isRequired,
    fields: PropTypes.array.isRequired,
    mappableItems: PropTypes.array.isRequired,
    onFieldSet: PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired
  };
  componentDidMount() {
    this.analyzeFields();
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevState.isValid !== this.state.isValid) {
      this.props.onValidate(this.state.isValid);
    }
  }
  analyzeFields() {
    let fields = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.props.fields;
    const {
      requirements,
      allowedDuplicates
    } = this.state;
    return new Promise(resolve => {
      this.setState(getFieldCounts(fields, requirements, allowedDuplicates));
      return resolve();
    });
  }
  selectMappedIndex(fieldIndex, mappedIndex) {
    const fields = update(this.props.fields, {
      [fieldIndex]: {
        MappedIndex: {
          $set: mappedIndex
        }
      }
    });
    return this.analyzeFields(fields).then(() => {
      this.props.onFieldSet(fields);
    });
  }
  render() {
    const {
      duplicateCounts,
      duplicateTotal,
      matchedCount,
      missingRequiredCount,
      requirements,
      unmatchedCount,
      importFieldsWithDuplicates
    } = this.state;
    return /*#__PURE__*/jsxs(Grid, {
      container: true,
      spacing: 1,
      children: [/*#__PURE__*/jsx(Grid, {
        item: true,
        xs: 12,
        children: /*#__PURE__*/jsx(Container, {
          children: /*#__PURE__*/jsxs(Table, {
            stickyHeader: true,
            children: [/*#__PURE__*/jsx(TableHead, {
              children: /*#__PURE__*/jsxs(TableRow, {
                children: [/*#__PURE__*/jsx(TableCell, {
                  children: /*#__PURE__*/jsx(Typography, {
                    children: /*#__PURE__*/jsxs(Box, {
                      fontWeight: "fontWeightBold",
                      component: "span",
                      children: [this.props.importTypeLabel, " Import Field"]
                    })
                  })
                }), /*#__PURE__*/jsx(TableCell, {
                  children: /*#__PURE__*/jsx(Typography, {
                    children: /*#__PURE__*/jsx(Box, {
                      fontWeight: "fontWeightBold",
                      component: "span",
                      children: "Scoir Field"
                    })
                  })
                })]
              })
            }), /*#__PURE__*/jsx(TableBody, {
              children: this.props.fields.map((field, fieldIdx) => /*#__PURE__*/jsx(ColumnMapperRow, {
                field: field,
                duplicateCount: duplicateCounts[field.MappedIndex],
                mappableItems: this.props.mappableItems,
                onSelectMappedIndex: mappedIndex => this.selectMappedIndex(fieldIdx, mappedIndex)
              }, fieldIdx))
            })]
          })
        })
      }), /*#__PURE__*/jsx(Grid, {
        item: true,
        xs: 12,
        children: /*#__PURE__*/jsx(Legend, {
          matchedCount: matchedCount,
          importFieldsWithDuplicates: importFieldsWithDuplicates,
          duplicateTotal: duplicateTotal,
          unmatchedCount: unmatchedCount,
          missingRequiredCount: missingRequiredCount,
          requirements: requirements
        })
      })]
    });
  }
}

export { ColumnMapper };
//# sourceMappingURL=index.js.map
